import { formatDistanceToNow } from 'date-fns';

import { TGroupSummary, TSlideDiff, TSnapshotDiff, TSnapshotMeta } from 'types/story';

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

export const publishedAgo = (iso?: string | null): string | null => {
  if (!iso || Number.isNaN(Date.parse(iso))) {
    return null;
  }
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
};

const joinList = (parts: string[]): string => {
  if (parts.length < 2) {
    return parts.join('');
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

type TSchedulable = Pick<TSnapshotMeta, 'startsAt' | 'endsAt'>;

const scheduleText = (snapshot: TSchedulable): string => {
  if (!snapshot.startsAt && !snapshot.endsAt) {
    return 'No schedule limits';
  }
  const from = snapshot.startsAt ? formatDate(snapshot.startsAt) : 'Immediately';
  const to = snapshot.endsAt ? formatDate(snapshot.endsAt) : 'No end';
  return `${from} → ${to}`;
};

const audienceText = (segmentId: string | null): string => (segmentId ? 'Targeted' : 'All users');

const boundText = (iso: string | null, unset: string): string => (iso ? formatDate(iso) : unset);

type TDescribable = Pick<TSnapshotMeta, 'slideCount' | 'segmentId'> & TSchedulable;

export const metaLines = (snapshot: TDescribable): string[] => [
  plural(snapshot.slideCount, 'slide'),
  audienceText(snapshot.segmentId),
  scheduleText(snapshot),
];

export const metaText = (snapshot: TDescribable): string => metaLines(snapshot).join(' · ');

const slidesText = (slides: TSlideDiff): string => {
  const parts: string[] = [];
  if (slides.added > 0) {
    parts.push(`${slides.added} added`);
  }
  if (slides.removed > 0) {
    parts.push(`${slides.removed} removed`);
  }
  if (slides.changed > 0) {
    parts.push(`${slides.changed} changed`);
  }
  if (slides.reordered) {
    parts.push('reordered');
  }
  return parts.join(', ');
};

const FIELD_LABEL: Record<string, string> = {
  title: 'title',
  cover: 'cover',
  schedule: 'schedule',
  audience: 'audience',
  pinned: 'pinning',
  items: 'slides',
};

export const pendingSummary = (pending: TSnapshotDiff): string =>
  pending.fields.map((field) => FIELD_LABEL[field] ?? field).join(', ');

export const describePendingChanges = (group: TGroupSummary): string[] => {
  const { pending, liveMeta } = group;
  if (!pending || !liveMeta) {
    return [];
  }

  const lines: string[] = [];
  if (pending.fields.includes('title')) {
    lines.push(`Title: “${liveMeta.title || '—'}” → “${group.title || '—'}”`);
  }
  if (pending.fields.includes('cover')) {
    lines.push(group.coverMediaUrl ? 'Cover image replaced' : 'Cover image removed');
  }
  if (pending.fields.includes('schedule')) {
    if (liveMeta.startsAt !== group.startsAt) {
      lines.push(
        `Starts: ${boundText(liveMeta.startsAt, 'immediately')} → ${boundText(group.startsAt, 'immediately')}`
      );
    }
    if (liveMeta.endsAt !== group.endsAt) {
      lines.push(
        `Ends: ${boundText(liveMeta.endsAt, 'never')} → ${boundText(group.endsAt, 'never')}`
      );
    }
  }
  if (pending.fields.includes('audience')) {
    lines.push(`Audience: ${audienceText(liveMeta.segmentId)} → ${audienceText(group.segmentId)}`);
  }
  if (pending.fields.includes('pinned')) {
    lines.push(group.pinned ? 'Pinned to the front' : 'No longer pinned');
  }
  if (pending.fields.includes('items')) {
    lines.push(`Slides: ${slidesText(pending.slides)}`);
  }
  return lines;
};

type TPublishAffordance = {
  label: string;
  isEnabled: boolean;
  reason: string | null;
  tone: 'info' | 'warning';
};

export const publishAffordance = (group: TGroupSummary): TPublishAffordance => {
  const label = 'Publish';

  if (group.status === 'DISABLED') {
    return {
      label,
      isEnabled: false,
      reason: 'Switch the group on to publish again.',
      tone: 'warning',
    };
  }

  const missing: string[] = [];
  if (!group.title.trim()) {
    missing.push('an English title');
  }
  // `TGroupSummary.coverMediaUrl` is already resolved to `en`, so this is the English-cover gate.
  if (!group.coverMediaUrl) {
    missing.push('an English cover image');
  }
  if (group.slideCount === 0) {
    missing.push('at least one slide');
  }
  if (missing.length > 0) {
    return {
      label,
      isEnabled: false,
      reason: `Needs ${joinList(missing)} to go live.`,
      tone: 'warning',
    };
  }

  if (group.hasLive && !group.pending) {
    return { label, isEnabled: false, reason: 'No unpublished changes.', tone: 'info' };
  }

  return { label, isEnabled: true, reason: null, tone: 'info' };
};
